/**
 * English language requirement. The LEVEL varies by stage — A1 at entry
 * clearance, A2 at the first extension, B1 at settlement rising to B2 on
 * 26 March 2027 (register §5).
 *
 * The B1→B2 change needs no code: it is two rules in the ruleset with
 * different `effective_from`/`effective_to` windows, and the engine already
 * selects rules effective at `facts.as_of` (§8). That is what "rules are
 * data" is supposed to buy.
 *
 * Never name a test provider: HC 1691 replaced "Ecctis" with a generic
 * description from 26 March 2026 (register §5).
 */
import type { EvaluatePredicate } from "./predicate.js";

/** CEFR levels, ordered — a higher level satisfies a lower requirement. */
const ORDER = ["none", "A1", "A2", "B1", "B2"] as const;
type Level = (typeof ORDER)[number];

function rank(level: string): number {
  const index = ORDER.indexOf(level as Level);
  if (index === -1) throw new Error(`Unknown English level: ${level}`);
  return index;
}

export const english_at_level: EvaluatePredicate = (facts, rule, ctx) => {
  const required = String(ctx.paramFigure(rule, "level_figure"));

  // Exemption is assessed by its own rules; here it simply removes the need.
  if (facts.english.exemption !== "none") {
    return {
      status: "NOT_APPLICABLE",
      notes: ["An exemption from the English language requirement has been indicated."],
    };
  }

  if (facts.english.level_held === null) {
    return {
      status: "PENDING_EVIDENCE",
      value: required,
      notes: [`The rules ask for English at level ${required} at this stage.`],
    };
  }

  const held = facts.english.level_held;
  if (rank(held) >= rank(required)) {
    return { status: "MET", value: required, notes: [`Level ${required} is required at this stage.`] };
  }

  return {
    status: "NOT_MET",
    value: required,
    notes: [
      `The rules ask for English at level ${required} at this stage. A qualification at a higher level also satisfies it.`,
    ],
  };
};

/** Exemption by nationality — a majority-English-speaking country. */
export const english_exempt_by_nationality: EvaluatePredicate = (facts) => {
  if (facts.english.exemption === "nationality") {
    return { status: "MET", notes: ["Exemption by nationality has been indicated."] };
  }
  return { status: "NOT_APPLICABLE" };
};

/** Exemption by academic qualification taught in English, age, or medical grounds. */
export const english_exempt_other: EvaluatePredicate = (facts) => {
  const exemption = facts.english.exemption;
  if (exemption === "qualification" || exemption === "age" || exemption === "medical") {
    return { status: "MET", notes: [`Exemption on ${exemption} grounds has been indicated.`] };
  }
  return { status: "NOT_APPLICABLE" };
};
